import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import {
  addAutomation as addAutomationAction,
  deleteAutomation as deleteAutomationAction,
  deleteAutomationTrigger as deleteAutomationTriggerAction,
  setAutomationSidebarVisible as setAutomationSidebarVisibleAction,
  setSelectedAutomation as setSelectedAutomationAction,
  storeAutomations,
  updateAutomation as updateAutomationAction,
  updateAutomationTrigger as updateAutomationTriggerAction,
  updateMultipleAutomations as updateMultipleAutomationsAction,
} from "../../actions/automation";
import { number, notifyIcon, quote } from "../../config";
import {
  addTriggerTask,
  addUpdateAutomationGroup as addUpdateAutomationGroupAPI,
  deleteAutomation as deleteAutomationAPI,
  deleteAutomationTrigger as deleteAutomationTriggerAPI,
  getAutomationDetails as getAutomationDetailsAPI,
  getAutomations as getAutomationsAPI,
  updateAutomation as updateAutomationAPI,
  updateAutomationTrigger as updateAutomationTriggerAPI,
} from "../../shared/services/automationService";
import { getWorkflowDefaults } from "../../shared/services/workflow.services";
import { getNotification } from "../../utils/common";
import { addUsersGroup, getUsersGroupDetails, updateUsersGroup } from "../Admin/admin.service";
import {
  buildLastSavedTriggerUpdatePayloadMap,
  buildUpdateAutomationTriggerPayload,
  diffUpdateAutomationTriggerPayload,
  getTriggerPayloadMapKey,
  isDynamicOrStaticGroup,
  isNewId,
  isSavedId,
  isAutomationAudienceEmptyForTrigger,
  getAutomationFrequencyTitle,
  normalizeAutomationTriggerShape,
  synchronizeTriggerKeys,
} from "./automation.helper";
import { USER_PROFILE_AUTOMATION_ALL_TEAMS_KEY } from "../UserProfileSetting/UserProfileLeftSetting/userProfileAutomation.constants";
import { loadState, saveState } from "../../shared/services/session.service";

/** Removes undefined entries from partial API responses so object spread does not overwrite with undefined. */
function stripUndefinedResponseFields(response) {
  if (!response || typeof response !== "object") {
    return {};
  }
  return Object.fromEntries(Object.entries(response).filter(([, v]) => v !== undefined));
}

/** Resets last-saved trigger payload map when switching to a different automation. */
function ensureLastSavedTriggerUpdateScope(lastSavedRef, automationId) {
  if (automationId == null) return;
  if (lastSavedRef.current.automationId !== automationId) {
    lastSavedRef.current = { automationId, lastSavedPayloadByTriggerId: {} };
  }
}

/** Full `updateAutomationTrigger` payload shape for one trigger, including optional display name for diffs. */
function buildTriggerUpdatePayloadWithName(trigger, automation, userId) {
  const payload = buildUpdateAutomationTriggerPayload(trigger, automation, userId);
  if (trigger.TriggerName != null) {
    payload.name = trigger.TriggerName;
  }
  return payload;
}

/** Compares last saved vs current trigger payload; returns the PUT body and whether anything changed. */
function computeTriggerSaveApiDiff(lastSavedRef, trigger, automation, userId) {
  ensureLastSavedTriggerUpdateScope(lastSavedRef, automation.id);
  const currentPayload = buildTriggerUpdatePayloadWithName(trigger, automation, userId);
  const triggerKey = getTriggerPayloadMapKey(trigger.TriggerId);
  let lastSavedPayload = triggerKey ? lastSavedRef.current.lastSavedPayloadByTriggerId[triggerKey] : undefined;
  if (trigger.recurrenceDisabledChangedByUser && lastSavedPayload) {
    lastSavedPayload = { ...lastSavedPayload };
    delete lastSavedPayload.isRecurrenceDisabled;
  }
  const apiPayload = diffUpdateAutomationTriggerPayload(lastSavedPayload, currentPayload);
  const hasChangesForApi = Object.keys(apiPayload).some((k) => k !== "triggerId");
  return { currentPayload, apiPayload, hasChangesForApi };
}

/** After a successful save, merge fields we actually sent into the stored last-saved copy for the next diff. */
function mergeLastSavedPayloadWithApiRequest(mergedPayload, apiPayload) {
  for (const [k, v] of Object.entries(apiPayload)) {
    if (k !== "triggerId" && k !== "updatedBy") {
      mergedPayload[k] = v;
    }
  }
}

/** Pushes the synchronized trigger into controlled selection or into automation.triggers. */
function commitSynchronizedTriggerToState(triggerId, synchronizedTrigger, setSelectedTrigger, setCurrentAutomation) {
  if (typeof setSelectedTrigger === "function") {
    setSelectedTrigger(synchronizedTrigger);
    return;
  }
  setCurrentAutomation((prev) => ({
    ...prev,
    triggers: prev.triggers.map((t) => (t.TriggerId === triggerId ? synchronizedTrigger : t)),
  }));
}

const AutomationContext = createContext();

export const AutomationProvider = ({children, projectId, shouldFetchAutomations = true, listSource = "team", userProfileAllTeamsFilterEnabled = false}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const { automations } = useSelector((state) => state.automation);
  const selectedAutomation = useSelector((state) => state.automation.selectedAutomation);
  const showSidebar = useSelector((state) => state.automation.showSidebar);
  const { assigneeList } = useSelector((state) => state.tasks.defaultDetails);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSavedData, setLastSavedData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [workflowDefaults, setWorkflowDefaults] = useState(null);
  const loadingRef = useRef(false);
  const lastSavedTriggerUpdateRef = useRef({ automationId: null, lastSavedPayloadByTriggerId: {} });

  const isUserProfileListSource = listSource === "userProfile";

  const [allTeamsChecked, setAllTeamsCheckedState] = useState(() => {
    if (!isUserProfileListSource || !userProfileAllTeamsFilterEnabled) return false;
    return loadState(USER_PROFILE_AUTOMATION_ALL_TEAMS_KEY)?.allTeamsChecked === true;
  });

  /**
   * Update loading reference
   * @returns {void}
   * @author Bhavana
   */
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  /**
   * Update all teams checked state
   * @returns {void}
   * @author Bhavana
   */
  useEffect(() => {
    if (isUserProfileListSource && !userProfileAllTeamsFilterEnabled) {
      setAllTeamsCheckedState(false);
    }
  }, [isUserProfileListSource, userProfileAllTeamsFilterEnabled]);

  /**
   * Set all teams checked
   * @param {function} next - The next function
   * @returns {void}
   * @author Bhavana
   */
  const setAllTeamsChecked = useCallback(
    (next) => {
      setAllTeamsCheckedState((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        if (isUserProfileListSource && userProfileAllTeamsFilterEnabled) {
          saveState(USER_PROFILE_AUTOMATION_ALL_TEAMS_KEY, { allTeamsChecked: value });
        }
        return value;
      });
    },
    [isUserProfileListSource, userProfileAllTeamsFilterEnabled]
  );

  const { automationId } = useParams();

  const extractTriggersForCard = (automation) => {
    const triggersArray = automation.Triggers || [];
    if (Array.isArray(triggersArray)) {
      const triggers = triggersArray
        .filter((trigger) => trigger && trigger.TriggerName && trigger.TriggerId)
        .map((trigger) => {
          const transformedInstances = (trigger.Instances || []).map((instance, index) => {
            const totalTasks = instance.TotalTasks || 0;
            const completedTasks = instance.CompletedTasks || 0;
            const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

            let formattedDate = "N/A";
            if (instance.CreatedOn) {
              const date = new Date(instance.CreatedOn);
              if (!isNaN(date.getTime())) {
                formattedDate = date.toLocaleDateString();
              }
            }

            const transformedInstance = {
              id: instance.TaskId,
              number: index + 1,
              date: formattedDate,
              parentId: instance.ProjectTaskID || "N/A",
              users: totalTasks,
              progress: progress,
              originalData: instance,
            };

            return transformedInstance;
          });

          const result = {
            ...trigger,
            frequency: getAutomationFrequencyTitle(trigger),
            isValid: true,
            instances: transformedInstances,
          };

          return result;
        });

      return triggers;
    }
    return [];
  };

  /** Helper function to determine automation status */
  const getAutomationStatus = (groupId, triggersCount) => {
    if (groupId && triggersCount > 0) {
      return "healthy";
    } else if (groupId || triggersCount > 0) {
      return "warning";
    } else {
      return "critical";
    }
  };

  /** Fetch all automations (single request; no limit/offset). Team vs profile scope via teamId when needed. */
  const fetchAutomations = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }

    setLoading(true);
    loadingRef.current = true;

    let apiParams;
    if (isUserProfileListSource) {
      apiParams = {};
      if (!allTeamsChecked && projectId != null && projectId !== "") {
        apiParams.teamId = projectId;
      }
    } else {
      apiParams = {
        teamId: projectId || user?.myProjectId || null,
      };
    }

    let response;
    try {
      response = await getAutomationsAPI(apiParams);
    } catch (e) {
      setLoading(false);
      loadingRef.current = false;
      return;
    }

    if (response && response.length > number.ZERO) {
      const transformedAutomations = response.map((automation) => {
        const triggers = extractTriggersForCard(automation);

        const transformedAutomation = {
          id: automation.Id,
          name: automation.Name,
          quickInfo: automation.QuickInfo || automation.Description,
          icon: automation.Icon,
          teamId: automation.TeamId,
          isPersonal: automation.TeamId === user?.myProjectId,
          createdBy: automation.UserMode,
          triggers: triggers,
          audience: automation.GroupId
            ? {
                type: "preset",
                groupId: automation.GroupId,
                selectedGroup: {
                  name: automation.GroupName,
                  isDynamic: automation.IsDynamic,
                },
                users: [],
                userCount: automation.MembersCount,
              }
            : {
                type: "preset",
                groupId: null,
                selectedGroup: null,
                users: [],
                userCount: 0,
              },
          activeTriggersCount: triggers.length,
          status: getAutomationStatus(automation.GroupId, triggers.length),
          teamCode: automation.ProjectCode || "",
          workspaceName: automation.WorkspaceName || "",
          automationProjectId: automation.ProjectId ?? automation.projectId ?? automation.TeamId ?? automation.teamId,
          projectName: automation.ProjectName || automation.projectName || "",
          isPersonalProject:
            Number(automation.IsPersonalProject ?? automation.isPersonalProject) === number.ONE ? number.ONE : number.ZERO,
          startDate: null,
          endDate: null,
          createdAt: automation.CreatedOn,
          updatedAt: automation.UpdatedOn,
          isDisabled: !!automation.IsDisabled,
          automationAudience: automation.AutomationAudience,
          dynamicVariableIds: automation.DynamicVariableIds,
          dynamicVariableValues: automation.DynamicVariableValues,
        };

        return transformedAutomation;
      });

      dispatch(storeAutomations(transformedAutomations));
    } else {
      dispatch(storeAutomations([]));
    }

    setLoading(false);
    loadingRef.current = false;
  }, [projectId, user?.myProjectId, allTeamsChecked, isUserProfileListSource, dispatch]);

  const refreshAutomations = useCallback(async () => {
    await fetchAutomations();
  }, [fetchAutomations]);

  /** Fetch detailed automation data including triggers and audience */
  const fetchAutomationDetails = useCallback(
    async (automationId) => {
      const response = await getAutomationDetailsAPI({ automationId });

      if (response && response.data) {
        const automationDetails = response.data.AutomationDetails;
        const triggers = response.data.Triggers || [];

        let audience = null;

        if (automationDetails.GroupId) {
          const groupDetails = await getUsersGroupDetails(automationDetails.GroupId);
          if (groupDetails) {
            let users = [];
            let userCount = 0;

            if (groupDetails.IsDynamic) {
              userCount = groupDetails.UserCount || 0;
              users = [];
            } else if (groupDetails.UserIds) {
              const userIds = groupDetails.UserIds.split(",").filter((id) => id.trim());
              userCount = userIds.length;

              users = userIds.map((userId) => {
                const userDetails = assigneeList?.find((user) => user.value == userId.trim());
                return {
                  id: userId.trim(),
                  name: userDetails?.label || "",
                  email: userDetails?.Email || "",
                  jobRole: userDetails?.JobRole || "",
                  avatar: userDetails?.PhotoLinks?.small || "",
                  department: userDetails?.Department || "",
                  officeLocation: userDetails?.OfficeLocation || "",
                  team: userDetails?.Team || "",
                };
              });
            } else {
              userCount = groupDetails.UserCount || 0;
            }

            audience = {
              type: "preset",
              groupId: automationDetails.GroupId,
              selectedGroup: {
                id: groupDetails.GroupId || automationDetails.GroupId,
                name: groupDetails.Name,
                description: groupDetails.Description || "",
                userCount: userCount,
                users: users,
                createdBy: groupDetails.CreatedBy,
                createdAt: groupDetails.CreatedOn,
                isActive: groupDetails.IsActive,
                isDynamic: groupDetails.IsDynamic,
                isAdmin: groupDetails.IsAdmin === 1,
                type: groupDetails.IsAdmin === 1 ? "admin" : "user",
              },
              users: users,
              userCount: userCount,
            };
          } else {
            audience = {
              type: "preset",
              groupId: automationDetails.GroupId,
              selectedGroup: null,
              users: [],
              userCount: 0,
            };
          }
        }

        /* Helper function to convert comma-separated chip IDs to tag array format */
        const convertConfigToTags = (configString, taskNameChips, isParent = false) => {
          if (!configString || !taskNameChips || !Array.isArray(taskNameChips)) {
            return isParent
              ? [
                  { type: "Instance ID", value: "Instance ID" },
                  { type: "TEXT", value: " | " },
                  { type: "Schedule Name", value: "Schedule Name" },
                  { type: "TEXT", value: " | PARENT" },
                ]
              : [
                  { type: "Instance ID", value: "Instance ID" },
                  { type: "TEXT", value: " | " },
                  { type: "Count of", value: "Count of" },
                  { type: "TEXT", value: " | " },
                  { type: "Schedule Name", value: "Schedule Name" },
                ];
          }

          const chipIds = configString
            .split(",")
            .map((id) => id.trim())
            .filter((id) => id);

          if (chipIds.length === 0) {
            return isParent
              ? [
                  { type: "Instance ID", value: "Instance ID" },
                  { type: "TEXT", value: " | " },
                  { type: "Schedule Name", value: "Schedule Name" },
                  { type: "TEXT", value: " | PARENT" },
                ]
              : [
                  { type: "Instance ID", value: "Instance ID" },
                  { type: "TEXT", value: " | " },
                  { type: "Count of", value: "Count of" },
                  { type: "TEXT", value: " | " },
                  { type: "Schedule Name", value: "Schedule Name" },
                ];
          }

          /* Convert chip IDs to tag array with separators */
          const tags = [];
          chipIds.forEach((chipId, index) => {
            const chip = taskNameChips.find((c) => c.Id === parseInt(chipId));
            if (chip) {
              if (index > 0) {
                tags.push({ type: "TEXT", value: " | " });
              }
              tags.push({
                type: chip.ChipName,
                value: chip.ChipName,
              });
            }
          });

          if (isParent && tags.length > 0) {
            const lastTag = tags[tags.length - 1];
            if (lastTag.value !== " | PARENT") {
              tags.push({ type: "TEXT", value: " | PARENT" });
            }
          }

          return tags.length > 0
            ? tags
            : isParent
            ? [
                { type: "Instance ID", value: "Instance ID" },
                { type: "TEXT", value: " | " },
                { type: "Schedule Name", value: "Schedule Name" },
                { type: "TEXT", value: " | PARENT" },
              ]
            : [
                { type: "Instance ID", value: "Instance ID" },
                { type: "TEXT", value: " | " },
                { type: "Count of", value: "Count of" },
                { type: "TEXT", value: " | " },
                { type: "Schedule Name", value: "Schedule Name" },
              ];
        };

        /* Transform triggers to include ParentTaskNameTags and ChildTaskNameTags from config */
        const transformedTriggers = triggers.map((trigger) => {
          const taskNameChips = response.data.TaskNameChips || [];
          const normalized = normalizeAutomationTriggerShape(trigger) || trigger;

          return {
            ...normalized,
            ParentTaskNameTags: convertConfigToTags(trigger.ParentTaskNameConfig, taskNameChips, true),
            ChildTaskNameTags: convertConfigToTags(trigger.ChildTaskNameConfig, taskNameChips, false),
            AutomationWorkflowId: trigger.AutomationWorkflowId,
            AutomationWorkflowName: trigger.AutomationWorkflowName || (trigger.AutomationWorkflowId && trigger.WorkflowId === trigger.AutomationWorkflowId ? trigger.WorkflowName : null),
            ParentWorkflowId: trigger.ParentWorkflowId ?? null,
            frequency: getAutomationFrequencyTitle(trigger),
            isValid: true,
          };
        });

        const automationWorkflowId = automationDetails.AutomationWorkflowId || 
          automationDetails.WorkflowId || 
          (transformedTriggers.length > 0 ? transformedTriggers[0]?.AutomationWorkflowId : null);

        const transformedAutomation = {
          id: automationDetails.Id,
          name: automationDetails.Name,
          quickInfo: automationDetails.QuickInfo,
          icon: automationDetails.Icon,
          teamId: automationDetails.TeamId,
          isPersonal: !automationDetails.TeamId,
          createdBy: automationDetails.UserMode,
          status: automationDetails.IsDisabled ? "inactive" : "active",
          teamCode: "",
          workspaceName: "",
          startDate: null,
          endDate: null,
          createdAt: automationDetails.CreatedOn,
          updatedAt: automationDetails.UpdatedOn,
          AutomationWorkflowId: automationWorkflowId,

          audience: audience,

          triggers: transformedTriggers,
          isDisabled: automationDetails.IsDisabled,
          activeTriggersCount: transformedTriggers.length,
          taskNameChips: response.data.TaskNameChips || [],
          triggerTypes: response.data.TriggerTypes || [],
        };

        lastSavedTriggerUpdateRef.current = buildLastSavedTriggerUpdatePayloadMap(transformedAutomation, user?.id);

        return transformedAutomation;
      }
      return null;
    },
    [assigneeList, user?.id]
  );

  /** Handle adding new automation */
  const handleAddAutomation = useCallback(async (automationData) => {
    if (automationData) {
      const newAutomation = {
        id: automationData.id || null,
        ...automationData,
        triggers: [],
        audience: {
          type: "preset",
          groupId: null,
          selectedGroup: null,
          customGroup: {
            name: "",
            description: "",
            members: [],
            filters: {
              jobRole: "",
              department: "",
              officeLocation: "",
              team: "",
            },
          },
          users: [],
          userCount: 0,
        },
        activeTriggersCount: 0,
        status: "active",
        isDisabled: 0,
        teamCode: "",
        workspaceName: "",
        startDate: null,
        endDate: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      dispatch(setSelectedAutomationAction(newAutomation));
      dispatch(setAutomationSidebarVisibleAction(true));
    }
  }, []);

  /** Handle editing automation - fetch details and open sidebar */
  const handleEditAutomation = useCallback(async (automation) => {
    const detailedAutomation = await fetchAutomationDetails(automation.id);
    if (detailedAutomation) {
      dispatch(setSelectedAutomationAction(detailedAutomation));
      dispatch(setAutomationSidebarVisibleAction(true));
      const savedData = {
        id: detailedAutomation.id,
        name: detailedAutomation.name,
        quickInfo: detailedAutomation.quickInfo,
        icon: detailedAutomation.icon,
        isDisabled: detailedAutomation.isDisabled,
      };
      setLastSavedData(savedData);
      setHasUnsavedChanges(false);
    } else {
      dispatch(setSelectedAutomationAction(automation));
      dispatch(setAutomationSidebarVisibleAction(true));
      const savedData = {
        id: automation.id,
        name: automation.name,
        quickInfo: automation.quickInfo,
        icon: automation.icon,
        isDisabled: automation.isDisabled,
      };
      setLastSavedData(savedData);
      setHasUnsavedChanges(false);
    }
  }, [fetchAutomationDetails]);

  /** Handle viewing automation - fetch details and open sidebar */
  const handleViewAutomation = useCallback(async (automation) => {
    const detailedAutomation = await fetchAutomationDetails(automation.id);
    if (detailedAutomation) {
      dispatch(setSelectedAutomationAction(detailedAutomation));
      dispatch(setAutomationSidebarVisibleAction(true));
      const savedData = {
        id: detailedAutomation.id,
        name: detailedAutomation.name,
        quickInfo: detailedAutomation.quickInfo,
        icon: detailedAutomation.icon,
        isDisabled: detailedAutomation.isDisabled,
      };
      setLastSavedData(savedData);
      setHasUnsavedChanges(false);
    } else {
      dispatch(setSelectedAutomationAction(automation));
      dispatch(setAutomationSidebarVisibleAction(true));
      const savedData = {
        id: automation.id,
        name: automation.name,
        quickInfo: automation.quickInfo,
        icon: automation.icon,
        isDisabled: automation.isDisabled,
      };
      setLastSavedData(savedData);
      setHasUnsavedChanges(false);
    }
  }, [fetchAutomationDetails]);

  /** Close sidebar and reset state */
  const handleCloseSidebar = useCallback(() => {
    dispatch(setAutomationSidebarVisibleAction(false));
    dispatch(setSelectedAutomationAction(null));
    setLastSavedData(null);
    setHasUnsavedChanges(false);
    lastSavedTriggerUpdateRef.current = { automationId: null, lastSavedPayloadByTriggerId: {} };
  }, []);

  /** Handle deleting automation */
  const handleDeleteAutomation = useCallback(async (automationId) => {
    if (!automationId) return false;

    setLoading(true);

    const response = await deleteAutomationAPI({ automationId });

    if (response && response.success) {
      dispatch(deleteAutomationAction(automationId));
      setLastSavedData(null);
      setHasUnsavedChanges(false);
      handleCloseSidebar();
      setLoading(false);
      return true;
    }

    setLoading(false);
    return false;
  }, []);

  /** Auto-save automation changes immediately */
  const handleAutoSave = useCallback(
    async (fieldName, value) => {
      if (!lastSavedData || !lastSavedData.id) {
        return;
      }

      if (fieldName !== "icon" && fieldName !== "audience" && !hasUnsavedChanges) {
        return;
      }

      const existingAutomation = automations.find((automation) => automation.id === lastSavedData.id);
      if (!existingAutomation) {
        return;
      }

      if (fieldName === "audience") {
        const audience = value;
        if (audience?.selectedGroup?.id) {
          setLoading(true);

          const userMode = isDynamicOrStaticGroup(audience.selectedGroup.isDynamic) ? number.ONE : number.TWO;
          const response = await addUpdateAutomationGroupAPI({
            automationId: lastSavedData.id,
            userMode,
            companyId: user?.companyId,
            groupId: audience.selectedGroup.id,
          });

          if (response) {
            const updatedAutomation = {
              ...existingAutomation,
              audience: {
                ...audience,
                groupId: audience.selectedGroup.id,
              },
              updatedAt: new Date().toISOString(),
            };
            dispatch(updateAutomationAction(updatedAutomation));
            setLastSavedData((prev) => ({ ...prev, audience }));
            if (refreshAutomations) {
              await refreshAutomations();
            }
          }
        }
        return;
      }

      const updateData = {
        name: fieldName === "name" ? value : lastSavedData.name,
        quickInfo: fieldName === "quickInfo" ? value : lastSavedData.quickInfo,
        icon: fieldName === "icon" ? value : lastSavedData.icon,
      };

      setLoading(true);
      const apiPayload = {
        automationId: lastSavedData.id,
        name: updateData.name,
        desc: updateData.quickInfo,
        icon: updateData.icon,
        updatedBy: user?.id,
      };

      const response = await updateAutomationAPI(apiPayload);

      if (response) {
        const updatedAutomation = {
          ...existingAutomation,
          ...updateData,
          updatedAt: new Date().toISOString(),
        };
        dispatch(updateAutomationAction(updatedAutomation));
        setHasUnsavedChanges(false);
        setLastSavedData((prev) => ({ ...prev, ...updateData }));
      }
      setLoading(false);
    },
    [lastSavedData, hasUnsavedChanges, user?.id, user?.companyId, automations, refreshAutomations, dispatch]
  );

  /** Track field changes for auto-save */
  const trackChanges = useCallback(
    (field, value) => {
      if (lastSavedData && lastSavedData[field] !== value) {
        setHasUnsavedChanges(true);
      } else if (!lastSavedData) {
        setHasUnsavedChanges(true);
      }
    },
    [lastSavedData]
  );

  /** Update last saved data and reset change tracking */
  const updateLastSavedData = useCallback((data) => {
    setLastSavedData(data);
    setHasUnsavedChanges(false);
  }, []);

  /** Set current automation data and initialize auto-save state */
  const setCurrentAutomationData = useCallback((automation) => {
    if (automation) {
      dispatch(setSelectedAutomationAction(automation));
      const savedData = {
        id: automation.id,
        name: automation.name,
        quickInfo: automation.quickInfo,
        icon: automation.icon,
        isDisabled: automation.isDisabled,
      };
      setLastSavedData(savedData);
      setHasUnsavedChanges(false);
    } else {
      setLastSavedData(null);
      setHasUnsavedChanges(false);
    }
  }, []);

  /** Handle trigger-related changes that require full card refresh */
  const handleTriggerRelatedChange = useCallback(
    async (changeType, triggerData = null) => {
      await fetchAutomations();
    },
    [fetchAutomations]
  );

  /** Sync automation audience details when a new group is created */
  const syncAutomationAudienceWithNewGroup = useCallback(
    async (newGroupId, newGroupName, newUserCount = 0) => {
      if (!newGroupId || !newGroupName) return;

      const automationsToUpdate = automations.filter((automation) => {
        const audience = automation?.audience;
        return audience?.groupId === newGroupId || audience?.selectedGroup?.id === newGroupId;
      });

      if (automationsToUpdate.length > 0) {
        automationsToUpdate.forEach((automation) => {
          const updatedAutomation = {
            ...automation,
            audience: {
              ...automation.audience,
              selectedGroup: {
                ...automation.audience?.selectedGroup,
                name: newGroupName,
                userCount: newUserCount,
              },
              userCount: newUserCount,
            },
          };

          dispatch(updateAutomationAction(updatedAutomation));
        });
      }
      await await refreshAutomations();
    },
    [automations]
  );

  /** Update all automations that use a specific group when group details change  */
  const updateAutomationsWithGroupChange = useCallback(
    async (groupId, groupName, groupDescription, userCount) => {
      if (!groupId || !groupName) return;

      const automationsToUpdate = automations.filter((automation) => {
        const audience = automation?.audience;
        return audience?.groupId === groupId || audience?.selectedGroup?.id === groupId;
      });

      if (automationsToUpdate.length > 0) {
        const updatedAutomations = automationsToUpdate.map((automation) => ({
          ...automation,
          audience: {
            ...automation.audience,
            selectedGroup: {
              ...automation.audience?.selectedGroup,
              name: groupName,
              description: groupDescription || automation.audience?.selectedGroup?.description,
              userCount: userCount || automation.audience?.selectedGroup?.userCount,
            },
            userCount: userCount || automation.audience?.userCount,
          },
        }));

        dispatch(updateMultipleAutomationsAction(updatedAutomations));
      }
    },
    [automations]
  );

  /** Handle group creation/selection for automation audience */
  const handleGroupAction = useCallback(
    async (groupData, actionType = "create") => {
      if (!selectedAutomation?.id || !user?.id || !user?.companyId) {
        return null;
      }

      const isDynamicAudienceGroup = (data) => {
        if (isDynamicOrStaticGroup(data?.isDynamic)) return true;
        if (data?.isDynamic === false || data?.isDynamic === 0) return false;
        return data?.userMode === number.ONE;
      };

      const automationUserModeForGroup = (data) => (isDynamicAudienceGroup(data) ? number.ONE : number.TWO);

      if (actionType === "create" && !isDynamicAudienceGroup(groupData)) {
        const groupPayload = {
          name: groupData.name,
          companyId: user.companyId,
          createdBy: user.id,
          description: groupData.description,
          isActive: true,
          teamId: projectId || user?.myProjectId,
        };

        const groupId = await addUsersGroup(groupPayload);
        if (!groupId) return null;

        if (groupData.members && groupData.members.length > 0) {
          const updatePayload = {
            groupId,
            name: groupData.name,
            description: groupData.description,
            isActive: true,
            action: "add",
            userId: user.id,
            teamId: projectId || user?.myProjectId,
          };

          await Promise.all(
            groupData.members.map((member) =>
              updateUsersGroup({
                ...updatePayload,
                memberId: member.id,
              })
            )
          );
        }

        const groupDetails = await getUsersGroupDetails(groupId);
        if (groupDetails) {
          const createdGroup = {
            id: groupId,
            name: groupDetails.Name,
            description: groupDetails.Description || "",
            type: "user",
            userCount: groupDetails.UserCount || 0,
            users: groupData.members || [],
            createdBy: user.id,
            icon: groupDetails.Icon || "groups",
            isDynamic: groupDetails.IsDynamic,
            isAdmin: groupDetails.IsAdmin === 1,
          };

          const existingAutomation = automations.find((automation) => automation.id === selectedAutomation.id);
          if (existingAutomation) {
            const updatedAutomation = {
              ...existingAutomation,
              audience: {
                type: "custom",
                groupId: groupId,
                selectedGroup: createdGroup,
                users: groupData.members || [],
                userCount: groupData.members?.length || 0,
              },
            };

            dispatch(updateAutomationAction(updatedAutomation));
          }
          await syncAutomationAudienceWithNewGroup(groupId, groupDetails.Name, groupDetails.UserCount || 0);

          return createdGroup;
        }
      }

      const payload = {
        automationId: selectedAutomation.id,
        userMode: automationUserModeForGroup(groupData),
        companyId: user.companyId,
        createdBy: user.id,
        teamId: projectId || user?.myProjectId,
      };

      if (actionType === "select" && groupData.groupId) {
        payload.groupId = groupData.groupId;
      }

      if (actionType === "create" && isDynamicAudienceGroup(groupData)) {
        payload.groupName = groupData.name;
        payload.groupDescription = groupData.description;
        payload.groupIcon = groupData.icon || "filter_list";
        payload.dynamicUserData = groupData.filters || {};
      }

      const response = await addUpdateAutomationGroupAPI(payload);

      if (response) {
        const existingAutomation = automations.find((automation) => automation.id === selectedAutomation.id);
        if (!existingAutomation) {
          return null;
        }

        const finalGroupData = response
          ? {
              ...response,
              ...(groupData.type && { type: groupData.type }),
              ...(groupData.isAdmin !== undefined && { isAdmin: groupData.isAdmin }),
            }
          : groupData;

        const updatedAutomation = {
          ...existingAutomation,
          audience: {
            type: actionType === "select" ? "preset" : "custom",
            groupId: actionType === "select" ? groupData.groupId : null,
            selectedGroup: finalGroupData,
            users: response?.users || groupData.members || [],
            userCount: response?.userCount || groupData.members?.length || 0,
          },
        };

        dispatch(updateAutomationAction(updatedAutomation));
        return finalGroupData;
      }

      return null;
    },
    [selectedAutomation, user, automations, projectId]
  );

  /** Fetch group details when we have a group ID */
  const fetchGroupDetails = useCallback(
    async (groupId) => {
      if (!groupId) return null;

      const groupDetails = await getUsersGroupDetails(groupId);
      if (groupDetails) {
        let userCount = 0;
        let users = [];

        if (groupDetails.Type === "dynamic") {
          const userIds = groupDetails.UserIds || [];
          userCount = userIds.length;
          users = userIds.map((userId) => {
            const userDetails = assigneeList?.find((user) => user.value == userId.trim());
            return {
              id: userId.trim(),
              name: userDetails?.label || `User ${userId.trim()}`,
              email: userDetails?.Email || "",
              jobRole: userDetails?.JobRole || "",
              avatar: userDetails?.PhotoLinks?.small || "",
              department: userDetails?.Department || "",
              officeLocation: userDetails?.OfficeLocation || "",
              team: userDetails?.Team || "",
            };
          });
        } else {
          userCount = groupDetails.UserCount || 0;
        }

        return {
          id: groupDetails.GroupId || groupId,
          name: groupDetails.Name,
          description: groupDetails.Description || "",
          userCount: userCount,
          users: users,
          createdBy: groupDetails.CreatedBy,
          createdAt: groupDetails.CreatedOn,
          isActive: groupDetails.IsActive,
          isDynamic: groupDetails.IsDynamic,
        };
      }

      return null;
    },
    [assigneeList]
  );

  /**
   * Handles basic trigger field changes
   */
  const handleTriggerChange = useCallback(
    (currentAutomation, setCurrentAutomation, triggerId, field, value, selectedTrigger, setSelectedTrigger) => {
      setCurrentAutomation((prev) => ({
        ...prev,
        triggers: prev.triggers.map((trigger) => {
          if (trigger.TriggerId === triggerId) {
            const updatedTrigger = { ...trigger, [field]: value };
            return synchronizeTriggerKeys(updatedTrigger);
          }
          return trigger;
        }),
      }));

      if (selectedTrigger && selectedTrigger.TriggerId === triggerId) {
        setSelectedTrigger((prev) => {
          if (prev && prev.TriggerId === triggerId) {
            const updatedSelectedTrigger = { ...prev, [field]: value };
            return synchronizeTriggerKeys(updatedSelectedTrigger);
          }
          return prev;
        });
      }
    },
    [synchronizeTriggerKeys]
  );

  /**
   * Main trigger field change handler
   */
  const handleTriggerFieldChange = useCallback(
    (currentAutomation, setCurrentAutomation, triggerId, field, value, selectedTrigger, setSelectedTrigger) => {
      if (!currentAutomation?.triggers) return;

      if (!triggerId || isNewId(triggerId)) {
        handleTriggerChange(currentAutomation, setCurrentAutomation, triggerId, field, value, selectedTrigger, setSelectedTrigger);
        return;
      }

      const fieldName = typeof field === "string" ? field : String(field);
      handleTriggerChange(currentAutomation, setCurrentAutomation, triggerId, fieldName, value, selectedTrigger, setSelectedTrigger);
    },
    [handleTriggerChange]
  );

  /**
   * Handles trigger now operation
   */
  const handleTriggerNow = useCallback(
    async (currentAutomation, setCurrentAutomation, trigger, selectedTrigger, setSelectedTrigger) => {
      if (!trigger.TriggerId || !currentAutomation.id || !user?.id) return;
      if (trigger.isTriggering) return;

      const audienceEmpty = await isAutomationAudienceEmptyForTrigger(currentAutomation, user);
      if (audienceEmpty) {
        getNotification(quote.AUDIENCE_HAS_NO_MEMBERS, notifyIcon.WARNING_ICON);
        return;
      }

      handleTriggerChange(currentAutomation, setCurrentAutomation, trigger.TriggerId, "isTriggering", true, selectedTrigger, setSelectedTrigger);

      const payload = {
        automationId: currentAutomation.id,
        triggerId: trigger.TriggerId,
        triggerAssignee: null,
        companyId: user.companyId,
      };

      if (currentAutomation.audience?.selectedGroup?.isDynamic && currentAutomation.audience?.groupId) {
        payload.groupId = currentAutomation.audience.groupId;
      }

      await addTriggerTask(payload);

      handleTriggerChange(currentAutomation, setCurrentAutomation, trigger.TriggerId, "isTriggering", false, selectedTrigger, setSelectedTrigger);
    },
    [user, handleTriggerChange]
  );
  /**
   * Handles trigger deletion
   */
  const handleDeleteTrigger = useCallback(
    async (currentAutomation, setCurrentAutomation, triggerId, selectedTrigger = null, setSelectedTrigger = null) => {
      if (!currentAutomation?.triggers) return;

      const triggerToDelete = currentAutomation.triggers.find((t) => t.TriggerId === triggerId);
      if (!triggerToDelete) return;

      const response = await deleteAutomationTriggerAPI({
        triggerId: triggerId,
        automationId: currentAutomation.id,
      });

      if (response) {
        if (!automationId) await refreshAutomations();
        setCurrentAutomation((prev) => ({
          ...prev,
          triggers: prev.triggers.filter((t) => t.TriggerId !== triggerId),
        }));

        if (selectedTrigger && setSelectedTrigger && selectedTrigger.TriggerId === triggerId) {
          setSelectedTrigger(null);
        }

        const deleteKey = getTriggerPayloadMapKey(triggerId);
        if (deleteKey) {
          delete lastSavedTriggerUpdateRef.current.lastSavedPayloadByTriggerId[deleteKey];
        }

        return true;
      }

      return false;
    },
    [projectId]
  );

  /**
   * Persists an existing trigger (PUT) or updates local state for an unsaved trigger.
   * Uses last-saved payload diffs to send minimal API bodies; helpers are defined at the top of this module.
   */
  const handleSaveTrigger = useCallback(
    async (currentAutomation, setCurrentAutomation, trigger, _selectedTrigger, setSelectedTrigger) => {
      const triggerId = trigger.TriggerId;

      if (!triggerId || isNewId(triggerId)) {
        const synced = synchronizeTriggerKeys(trigger);
        setCurrentAutomation((prev) => ({
          ...prev,
          triggers: prev.triggers.map((t) => (t.TriggerId === triggerId ? synced : t)),
        }));
        return true;
      }

      const { currentPayload, apiPayload, hasChangesForApi } = computeTriggerSaveApiDiff(
        lastSavedTriggerUpdateRef,
        trigger,
        currentAutomation,
        user?.id
      );

      if (!hasChangesForApi) {
        const noChangeKey = getTriggerPayloadMapKey(triggerId);
        if (noChangeKey) {
          lastSavedTriggerUpdateRef.current.lastSavedPayloadByTriggerId[noChangeKey] = { ...currentPayload };
        }
        commitSynchronizedTriggerToState(
          triggerId,
          synchronizeTriggerKeys(trigger),
          setSelectedTrigger,
          setCurrentAutomation
        );
        return true;
      }

      const response = await updateAutomationTriggerAPI(apiPayload);
      if (!automationId) {
        await refreshAutomations();
      }
      if (!response) {
        return false;
      }

      const responseClean = stripUndefinedResponseFields(response);
      const updatedTrigger = {
        ...trigger,
        ...responseClean,
        EndDate: response?.EndDate ?? response?.endDate ?? apiPayload.endDate ?? trigger.EndDate,
      };
      const synchronizedTrigger = synchronizeTriggerKeys(updatedTrigger);

      const mergedLastSavedPayload = buildTriggerUpdatePayloadWithName(synchronizedTrigger, currentAutomation, user?.id);
      mergeLastSavedPayloadWithApiRequest(mergedLastSavedPayload, apiPayload);
      const savedKey = getTriggerPayloadMapKey(triggerId);
      if (savedKey) {
        lastSavedTriggerUpdateRef.current.lastSavedPayloadByTriggerId[savedKey] = mergedLastSavedPayload;
      }

      commitSynchronizedTriggerToState(triggerId, synchronizedTrigger, setSelectedTrigger, setCurrentAutomation);
      return true;
    },
    [user?.id, synchronizeTriggerKeys, projectId, automationId, refreshAutomations]
  );

  const recordLastSavedTriggerUpdatePayload = useCallback((currentAutomation, trigger) => {
    if (!currentAutomation?.id || !trigger?.TriggerId || isNewId(trigger.TriggerId)) {
      return;
    }
    ensureLastSavedTriggerUpdateScope(lastSavedTriggerUpdateRef, currentAutomation.id);
    const triggerKey = getTriggerPayloadMapKey(trigger.TriggerId);
    if (!triggerKey) {
      return;
    }
    const lastSavedPayload = buildTriggerUpdatePayloadWithName(trigger, currentAutomation, user?.id);
    const existing = lastSavedTriggerUpdateRef.current.lastSavedPayloadByTriggerId[triggerKey];
    if (existing && existing.isRecurrenceDisabled !== undefined) {
      lastSavedPayload.isRecurrenceDisabled = existing.isRecurrenceDisabled;
    }
    lastSavedTriggerUpdateRef.current.lastSavedPayloadByTriggerId[triggerKey] = lastSavedPayload;
  }, [user?.id]);

  /**
   * Handles automation activation
   */
  const handleActivate = useCallback(
    async (currentAutomation, setCurrentAutomation) => {
      if (!currentAutomation?.id || !user?.id) return;

      const apiPayload = {
        automationId: currentAutomation.id,
        isDisabled: 0,
        updatedBy: user.id,
      };

      const response = await updateAutomationAPI(apiPayload);
      if (response) {
        const updatedAutomation = {
          ...currentAutomation,
          status: "active",
          isDisabled: false,
          updatedAt: new Date().toISOString(),
        };

        setCurrentAutomation(updatedAutomation);
        dispatch(updateAutomationAction(updatedAutomation));

        await refreshAutomations();

        return true;
      }

      return false;
    },
    [user?.id]
  );

  /**
   * Handles automation deactivation
   */
  const handleDeactivate = useCallback(
    async (currentAutomation, setCurrentAutomation) => {
      if (!currentAutomation?.id || !user?.id) return;

      const apiPayload = {
        automationId: currentAutomation.id,
        isDisabled: 1,
        updatedBy: user.id,
      };

      const response = await updateAutomationAPI(apiPayload);
      if (response) {
        const updatedAutomation = {
          ...currentAutomation,
          status: "inactive",
          isDisabled: true,
          updatedAt: new Date().toISOString(),
        };

        setCurrentAutomation(updatedAutomation);
        dispatch(updateAutomationAction(updatedAutomation));

        await refreshAutomations();

        return true;
      }

      return false;
    },
    [user?.id]
  );

  /**
   * Handles icon change
   */
  const handleIconChange = useCallback((currentAutomation, setCurrentAutomation, selectedIcon, workflowDefaults, trackChanges, handleAutoSave) => {
    let iconValue = selectedIcon;

    if (workflowDefaults?.workflowIcons) {
      const selectedIconObj = workflowDefaults.workflowIcons.find((iconObj) => iconObj.value === selectedIcon);
      if (selectedIconObj) {
        iconValue = selectedIconObj.value;
      }
    }

    setCurrentAutomation((prev) => ({
      ...prev,
      icon: iconValue,
    }));

    if (trackChanges && handleAutoSave) {
      trackChanges("icon", iconValue);
      setTimeout(() => {
        handleAutoSave("icon", iconValue);
      }, 0);
    }
  }, []);

  /** Fetch automations when project / profile scope changes */
  useEffect(() => {
    if (!shouldFetchAutomations) {
      return;
    }
    if (isUserProfileListSource) {
      const canFetch = allTeamsChecked || (projectId);
      if (!canFetch) {
        return;
      }
      fetchAutomations();
      return;
    }
    if (projectId !== undefined && projectId !== null) {
      fetchAutomations();
    }
  }, [projectId, shouldFetchAutomations, isUserProfileListSource, allTeamsChecked, fetchAutomations]);

  useEffect(() => {
    if (showSidebar && projectId !== undefined) {
      handleCloseSidebar();
    }
  }, [projectId]);

  /** Fetch workflow defaults for icons - shared across all automation components */
  useEffect(() => {
    const fetchWorkflowDefaults = async () => {
      if (user?.id && user?.myProjectId && user?.companyId) {
        const defaults = await getWorkflowDefaults({
          userId: user.id,
          projectId: user.myProjectId,
          companyId: user.companyId,
        });
        if (defaults) {
          setWorkflowDefaults(defaults);
        }
      }
    };

    fetchWorkflowDefaults();
  }, [user?.id]);

  /** Listen for group updates and update all affected automations */
  useEffect(() => {
    const handleGroupUpdateEvent = async (event) => {
      const { groupId, groupName, groupDescription, userCount } = event.detail;
      if (groupId && groupName) {
        await updateAutomationsWithGroupChange(groupId, groupName, groupDescription, userCount);
      }
    };

    window.addEventListener("automationGroupUpdated", handleGroupUpdateEvent);

    return () => {
      window.removeEventListener("automationGroupUpdated", handleGroupUpdateEvent);
    };
  }, [updateAutomationsWithGroupChange]);

  const value = {
    selectedAutomation,
    showSidebar,
    automations,
    loading,
    error,

    listSource,
    allTeamsChecked,
    setAllTeamsChecked,
    userProfileAllTeamsFilterEnabled,

    lastSavedData,
    hasUnsavedChanges,
    workflowDefaults,

    handleAddAutomation,
    handleEditAutomation,
    handleViewAutomation,
    handleCloseSidebar,
    refreshAutomations,
    fetchAutomationDetails,
    handleDeleteAutomation,
    handleAutoSave,
    trackChanges,
    updateLastSavedData,
    setCurrentAutomationData,
    handleGroupAction,
    syncAutomationAudienceWithNewGroup,
    updateAutomationsWithGroupChange,
    fetchGroupDetails,
    handleTriggerRelatedChange,
    handleTriggerFieldChange,
    handleTriggerNow,
    handleDeleteTrigger,
    handleSaveTrigger,
    recordLastSavedTriggerUpdatePayload,

    handleActivate,
    handleDeactivate,
    handleIconChange,
  };

  return <AutomationContext.Provider value={value}>{children}</AutomationContext.Provider>;
};

export const useAutomation = () => {
  const context = useContext(AutomationContext);
  if (!context) {
    throw new Error("useAutomation must be used within an AutomationProvider");
  }
  return context;
};
